import React from 'react';
import { ShieldAlertIcon, AlertCircle, Calendar } from 'lucide-react';
export function formatScheduledDate(date: Date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffTime = target.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1) return `In ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
}
export function ScheduledDateBadge({
  date
}: {
  date?: Date;
}) {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffTime = target.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  let label = formatScheduledDate(date);
  let className = '';
  if (diffDays === 0) {
    className = 'bg-gray-100 text-gray-600';
  } else if (diffDays === 1) {
    className = 'bg-blue-50 text-blue-700 border border-blue-200';
  } else if (diffDays > 1) {
    className = 'bg-indigo-50 text-indigo-700 border border-indigo-200';
  } else {
    className = 'text-gray-400 italic bg-gray-50/50';
  }
  return <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${className}`} data-id="element-2803" data-mp="1twa9wqrlagee">
      <Calendar size={10} data-id="element-2804" data-mp="2hnpiv2dvfvym" />
      {label}
    </span>;
}
export function SifBadge({
  size = 'default'
}: {
  size?: 'default' | 'large';
}) {
  if (size === 'large') {
    return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-700 text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-red-200 animate-pulse" data-id="element-2805" data-mp="27p03bg9g6nlj">
        <ShieldAlertIcon size={16} data-id="element-2806" data-mp="w8idw049pl8e" />
        SIF POTENTIAL
      </span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-700 text-white text-[10px] font-black uppercase tracking-wider shadow-sm shadow-red-200 ring-1 ring-red-800/20 animate-pulse" data-id="element-2807" data-mp="117v32rim34e5">
      <ShieldAlertIcon size={12} data-id="element-2808" data-mp="d3wsz392hhtl" />
      SIF
    </span>;
}
export function DataConfidenceBadge({
  confidence
}: {
  confidence: 'high' | 'medium' | 'low';
}) {
  if (confidence === 'high') return null;
  if (confidence === 'low') {
    return <span title="Limited data available — this risk score may underestimate actual danger" className="inline-flex items-center gap-1 bg-red-100 text-red-700 border-2 border-red-300 text-[10px] font-black uppercase px-2 py-0.5 rounded animate-pulse cursor-help shadow-sm shadow-red-100" data-id="element-2809" data-mp="3ed8jt5phfwm">
        <AlertCircle size={11} data-id="element-2810" data-mp="3lmchfiu7bate" /> Limited Data ⚠
      </span>;
  }
  return <span title="Some data sources are incomplete — risk score may not reflect full picture" className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 border-2 border-amber-300 text-[10px] font-bold uppercase px-2 py-0.5 rounded cursor-help shadow-sm shadow-amber-100" data-id="element-2811" data-mp="3gmq3f76jtqjj">
      <AlertCircle size={10} data-id="element-2812" data-mp="1mcrvjlfvsxk2" /> Partial Data
    </span>;
}
export function FreshnessBadge({
  lastUpdated
}: {
  lastUpdated: Date;
}) {
  const now = new Date();
  const diffMs = now.getTime() - lastUpdated.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor(diffMs / (1000 * 60));
  let label: string;
  let className: string;
  if (diffHours > 5) {
    label = 'Stale Data ⚠';
    className = 'bg-red-100 text-red-700 border-2 border-red-300 font-black uppercase animate-pulse shadow-sm shadow-red-100';
  } else if (diffHours > 2) {
    label = `${diffHours}h ago`;
    className = 'bg-amber-100 text-amber-700 border-2 border-amber-300 font-bold uppercase shadow-sm shadow-amber-100';
  } else if (diffHours > 0) {
    label = `${diffHours}h ago`;
    className = 'bg-gray-100 text-gray-500 font-medium';
  } else {
    label = `${diffMins}m ago`;
    className = 'bg-emerald-50 text-emerald-600 font-medium';
  }
  const isStale = diffHours > 5;
  const isAging = diffHours > 2;
  return <span title={isStale ? 'Data is over 5 hours old — risk score may be outdated and unreliable' : isAging ? 'Data is getting old — consider refreshing' : 'Data is recent'} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded cursor-help ${className}`} data-id="element-2813" data-mp="2md4xa40jk7rj">
      <AlertCircle size={10} data-id="element-2814" data-mp="3a9hm56prdtha" />
      {label}
    </span>;
}
/** Returns true if an area has data quality concerns (low confidence or stale data) */
export function hasDataGap(area: {
  dataConfidence: string;
  lastUpdated: Date;
}): boolean {
  const diffHours = (Date.now() - area.lastUpdated.getTime()) / (1000 * 60 * 60);
  return area.dataConfidence !== 'high' || diffHours > 5;
}